"use client";

/**
 * CreditExhaustedModal — shown when credits are fully exhausted.
 *
 * Presents plan upgrade options and a dismiss action.
 */

import { useRouter } from "next/navigation";
import {
  ANALYTICS_EVENTS,
  PRICES,
  PLAN_CREDITS,
  type SellablePlan,
} from "@bash/shared";
import { posthog } from "@/lib/posthog";
import { useCreditStore } from "@/stores/creditStore";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeaderRich,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VestraToken } from "@/components/VestraToken";
import { CheckIcon } from "@/components/icons/CheckIcon";

/**
 * The two tiers this modal offers, priced from the shared table.
 *
 * This is the moment a user is most likely to buy, so a stale figure here is
 * the most expensive kind: it advertised Pro at $39/5,000 and Max at
 * $197/30,000 long after /pricing had moved to $97/10,000 and $197/20,000.
 * Reading PRICES and PLAN_CREDITS is what keeps this honest — the same reason
 * /account/plan reads them.
 */
const OFFERED: SellablePlan[] = ["pro", "max"];

const PLANS = OFFERED.map((id, i) => ({
  name: id.charAt(0).toUpperCase() + id.slice(1),
  price: PRICES.USD[id].monthly.displayMonthly,
  highlight: i === 0,
  features: [
    `${PLAN_CREDITS[id].toLocaleString("en-US")} credits/mo`,
    ...(id === "pro"
      ? ["Priority support", "All integrations"]
      : ["Custom agents", "Dedicated support"]),
  ],
}));

/**
 * The balance line. A wallet can sit below zero (charges that settle after the
 * last turn, staging 3 Oct: -1.154), and "-1.15 cr remaining" reads as a debt
 * notice, not a balance: at or below zero the line says plainly what is left.
 * Above zero the person is refused because the balance cannot fund a turn
 * (CREDIT_AFFORD_GATE_V1), not because it is gone, and the line says that.
 */
export function creditsLeftLabel(balance: number): string {
  return balance > 0
    ? `${balance.toFixed(2)} cr left, not enough for a turn`
    : "0 credits left";
}

/** Never "used up" to someone who still holds credits. */
export function exhaustedTitle(balance: number): string {
  return balance > 0 ? "Not Enough Credits" : "Credits Used Up";
}

export function CreditExhaustedModal() {
  const router = useRouter();
  const showExhaustedModal = useCreditStore((s) => s.showExhaustedModal);
  const balance = useCreditStore((s) => s.balance);
  const dismissExhaustedModal = useCreditStore((s) => s.dismissExhaustedModal);

  return (
    <Dialog
      open={showExhaustedModal}
      onOpenChange={(open) => !open && dismissExhaustedModal()}
    >
      <DialogContent hideCloseButton className="sm:max-w-[440px] border">
        {/* Red accent bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-[#f87171]" />

        {/* Header */}
        <DialogHeaderRich
          icon={<VestraToken size={28} />}
          iconBgc={"bg-[#f87171]/10"}
          title={exhaustedTitle(balance)}
          description={"Upgrade your plan to keep going"}
        />

        <div className="mt-3 text-center">
          <span className="px-3 py-1 rounded-12 text-3.5 font-bold tabular-nums bg-current/10 text-[#f87171]">
            {creditsLeftLabel(balance)}
          </span>
        </div>

        {/* Plan cards */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {PLANS.map((plan) => (
            <button
              key={plan.name}
              onClick={() => {
                posthog.capture(ANALYTICS_EVENTS.UPGRADE_CTA_CLICKED, {
                  source: "credit_exhausted_modal",
                  plan: plan.name,
                });
                dismissExhaustedModal();
                router.push("/account/plan");
              }}
              className="text-left rounded-16 p-4 transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: plan.highlight
                  ? "var(--bg-muted)"
                  : "var(--bg-muted)",
                border: plan.highlight
                  ? "1px solid var(--border-strong)"
                  : "1px solid var(--border-subtle)",
              }}
            >
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4.5 font-bold text-fg-default">
                  ${plan.price}
                </span>
                <span className="text-3 text-fg-subtle">/mo</span>
              </div>
              <p
                className="text-3.5 font-semibold mb-2"
                style={{
                  color: plan.highlight
                    ? "var(--fg-default)"
                    : "var(--fg-muted)",
                }}
              >
                {plan.name}
              </p>
              <div className="space-y-1">
                {plan.features.map((f) => (
                  <p
                    key={f}
                    className="text-3 flex items-center gap-1.5 text-fg-subtle"
                  >
                    <CheckIcon
                      width={10}
                      height={10}
                      stroke="#22c55e"
                      strokeWidth="3"
                    />
                    {f}
                  </p>
                ))}
              </div>
            </button>
          ))}
        </div>

        {/* Actions */}
        <DialogFooter className="flex flex-col sm:flex-col">
          <Button
            className="w-full"
            onClick={() => {
              posthog.capture(ANALYTICS_EVENTS.UPGRADE_CTA_CLICKED, {
                source: "credit_exhausted_modal",
              });
              dismissExhaustedModal();
              router.push("/account/plan");
            }}
          >
            View Plans
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={dismissExhaustedModal}
          >
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
